<template>
<m-pageframe
    :title="title"
>
<v-container
    fluid
    style="padding-left: 15px; padding-right: 15px;"
>

    <v-text-field
        autocomplete="off"
        label="Search groups by name, description, ID, or status"
        prepend-icon="mdi-magnify"
        v-model="full_text"
        v-on:input="update"
        clearable
        style="margin-bottom: 20px;"
    >
    </v-text-field>

    <!-- Selected Groups Summary - Always visible when in bulk message mode -->
    <v-card
        v-if="add_to_bulk_message != null"
        class="mb-6"
        outlined
    >
        <v-card-title class="pb-2">
            <v-icon class="mr-2" color="primary">mdi-account-group</v-icon>
            Selected Groups
            <v-spacer/>
            <v-chip
                :color="selected_groups.length > 0 ? 'primary' : 'grey lighten-2'"
                :text-color="selected_groups.length > 0 ? 'white' : 'grey darken-1'"
                small
            >
                {{selected_groups.length}} selected
            </v-chip>
        </v-card-title>

        <v-card-text class="pt-0">
            <div v-if="selected_groups.length === 0" class="text--secondary">
                Select groups below to add their customers to your bulk message
            </div>
            <div v-else>
                <v-chip
                    v-for="(item, index) in selected_groups"
                    :key="'sg_'+index"
                    class="mr-2 mb-2"
                    close
                    color="primary"
                    outlined
                    v-on:click:close="onClickClose(item)"
                >
                    {{item.text}}
                </v-chip>
            </div>
        </v-card-text>

        <!-- Always show actions area to prevent layout shift -->
        <v-card-actions>
            <v-spacer/>
            <v-btn
                text
                color="error"
                v-on:click="clearAllSelections"
                :disabled="selected_groups.length === 0"
                :style="{ visibility: selected_groups.length > 0 ? 'visible' : 'hidden' }"
            >
                Clear All
            </v-btn>
            <v-btn
                :color="is_remove_mode ? 'warning' : 'primary'"
                v-on:click="onClickAddCustomersToBulkMessage"
                :disabled="selected_groups.length === 0"
            >
                <v-icon left>{{ is_remove_mode ? 'mdi-minus' : 'mdi-plus' }}</v-icon>
                {{ is_remove_mode ? 'Remove Recipients from Bulk Message' : 'Add Recipients to Bulk Message' }}
            </v-btn>
        </v-card-actions>
    </v-card>

    <!-- Enhanced Groups Table -->
    <v-data-table
        v-if="results != null"
        :headers="computedHeaders"
        :items="results"
        :items-per-page="50"
        :no-data-text="computedNoDataText"
        :footer-props="footerProps"
        class="enhanced-data-table enhanced-data-table--groups elevation-1"
    >


        <!-- Selection Column Template -->
        <template #item.selected="{ item }">
            <v-simple-checkbox
                v-if="add_to_bulk_message != null"
                :value="isSelected(item)"
                @input="onSelectionChange({ selected: $event, item })"
                color="primary"
                :ripple="false"
            />
        </template>

        <!-- Group Info Column -->
        <template #item.group_info="{ item }">
            <div
                class="group-row-content"
                :class="{ 'group-row-content--selected': isSelected(item) }"
                v-on:click="add_to_bulk_message != null ? toggleSelection(item) : null"
            >
                <div class="d-flex align-center">
                    <v-icon
                        class="mr-3"
                        :color="isSelected(item) ? 'primary' : 'grey darken-1'"
                        size="20"
                    >
                        mdi-account-group
                    </v-icon>
                    <div class="flex-grow-1">
                        <div class="group-name">{{item.label}}</div>
                        <div v-if="item.description" class="group-description">{{item.description}}</div>
                        <div v-else class="group-description text--disabled">No description available</div>
                    </div>
                </div>
            </div>
        </template>

        <!-- Status Column -->
        <template #item.status="{ item }">
            <v-chip
                v-if="item.status"
                small
                :color="getStatusColor(item.status)"
                text-color="white"
                class="status-chip"
            >
                {{ item.status }}
            </v-chip>
            <span v-else class="text--disabled">Unknown</span>
        </template>

        <!-- ID Column -->
        <template #item.id="{ item }">
            <v-chip
                small
                outlined
                color="grey darken-1"
                class="id-chip"
            >
                {{ item.id }}
            </v-chip>
        </template>

    </v-data-table>

</v-container>
</m-pageframe>
</template>

<script>
export default {
    name: 'crm-group-meta-data',
    data() {
        return {
            full_text: "",
            results: null,
            selected_groups: [],
            add_to_bulk_message: null,
        }
    },
    computed: {
        title() {
            const action = this.is_remove_mode ? 'Remove' : 'Add';
            if (this.add_to_bulk_message != null) {
                return `${action} recipients based on ${this.crm_name} groups`;
            }
            return `${this.crm_name} Groups`;
        },
        is_remove_mode() {
            return this.$route.query.mode === 'remove';
        },
        import_execution_mode() {
            return this.is_remove_mode ? 'REMOVE' : 'ADD_OR_UPDATE';
        },
        base_headers() {
            return [
                {
                    text: "Group Information",
                    value: "group_info",
                    sortable: false,
                    class: "group-info-column"
                },
                {
                    text: "Status",
                    value: "status",
                    width: "100px",
                    sortable: false,
                    class: "text-center"
                },
                {
                    text: "Group ID",
                    value: "id",
                    width: "120px",
                    sortable: false,
                    class: "text-center"
                }
            ];
        },

        computedHeaders() {
            let headers = [];

            // Add selection column if in bulk message mode
            if (this.add_to_bulk_message != null) {
                headers.push({
                    text: "",
                    value: "selected",
                    width: "60px",
                    sortable: false,
                    class: "text-center"
                });
            }

            headers.push(...this.base_headers);
            return headers;
        },

        computedNoDataText() {
            if (this.full_text && this.full_text.trim()) {
                return `No groups found matching "${this.full_text}"`;
            }
            return "No groups available";
        },

        footerProps() {
            return {
                itemsPerPageText: 'Groups per page:',
                itemsPerPageOptions: [25, 50, 100],
                disableItemsPerPage: false,
                disablePagination: false
            };
        },

        enhanced_headers() {
            let headers = [];

            if (this.add_to_bulk_message != null) {
                headers.push({
                    text: "",
                    value: "selected",
                    width: "60px",
                    sortable: false,
                    class: "text-center"
                });
            }

            headers.push(...this.base_headers);
            return headers;
        },
        no_data_message() {
            if (this.full_text && this.full_text.trim()) {
                return `No groups found matching "${this.full_text}"`;
            }
            return "No groups available";
        },
        organization_id() {
            return this.$route.params.organization_id;
        },
        integration_type() {
            return this.$route.params.integration_type;
        },
        crm_name() {
            return this.$rc.pretty(this.integration_type, 'enum_integration_name');
        },
    },
    mounted() {
        if (this.$route.query.add_to_bulk_message_id) {
            this.updateAddToBulkMessage();
        }
        this.update();
    },
    methods: {
        updateAddToBulkMessage() {
            if (this.$route.query.add_to_bulk_message_id == null) {
                this.add_to_bulk_message = null;
                return;
            }

            this.$rc.rest({
                'spinner': false,
                'endpoint': 'GET: /api/v1/bulk/messages/get-profile/:bulk_message_id',

                'params': {
                    'bulk_message_id': this.$route.query.add_to_bulk_message_id,
                },

                'on_success': (data) => {
                    this.add_to_bulk_message = data;
                },

                'on_error': 'Unable to get bulk message meta data',
            });
        },

        update() {
            this.$rc.rest(
                `GET: /api/v1/integrations/crm/groups/${this.organization_id}/${this.integration_type}`,
                {},
                (data) => {
                    this.results = data.groups.filter(group => {
                        if (!this.full_text) return true;
                        const search = this.full_text.toLowerCase();
                        return group.label.toLowerCase().includes(search) ||
                               (group.description && group.description.toLowerCase().includes(search)) ||
                               group.id.toLowerCase().includes(search) ||
                               (group.status && group.status.toLowerCase().includes(search));
                    });
                },
                () => {
                    this.$rc.alert('Error', 'Unable to load groups');
                }
            );
        },

        isSelected(item) {
            return this.selected_groups.some(g => g.value === item.id);
        },
        toggleSelection(item) {
            if (this.isSelected(item)) {
                this.selected_groups = this.selected_groups.filter(g => g.value !== item.id);
            } else {
                this.selected_groups.push({
                    text: item.label,
                    value: item.id,
                });
            }
        },
        onChangeSelected(selected, item) {
            if (selected) {
                this.selected_groups.push({
                    text: item.label,
                    value: item.id,
                });
            } else {
                this.selected_groups = this.selected_groups.filter(g => g.value !== item.id);
            }
        },

        onSelectionChange({ selected, item }) {
            this.onChangeSelected(selected, item);
        },
        onClickClose(item) {
            this.selected_groups = this.selected_groups.filter(g => g.value !== item.value);
        },
        clearAllSelections() {
            this.selected_groups = [];
        },
        getStatusColor(status) {
            if (!status) return 'grey';

            const statusLower = status.toLowerCase();
            switch (statusLower) {
                case 'active':
                    return 'success';
                case 'inactive':
                case 'disabled':
                    return 'orange darken-1';
                case 'archived':
                case 'deleted':
                    return 'error';
                default:
                    return 'blue';
            }
        },
        onClickAddCustomersToBulkMessage() {
            this.$rc.confirm({
                'title': 'Warning: Check your recipients carefully',
                'body': '<p>RedChirp does its best to create an accurate and complete list of customers in the group(s) you have specified. Despite our best efforts, however, such a list may be inaccurate or incomplete.</p><p>It is important that you check your recipients <b>very carefully</b> before sending any bulk message.</p>',
                'btn_text_ok': 'I understand',
                'btn_text_cancel': 'Cancel',
                'on_ok': () => {
                    this.doAddCustomersToBulkMessage();
                },
            });
        },

        doAddCustomersToBulkMessage() {
            this.$rc.startProcess({
                'type': 'crm-create-excel-preview-of-customers-in-group-for-bulk-message',

                'params': {
                    'organization_id': this.organization_id,
                    'content_type': this.integration_type,
                    'bulk_message_id': this.add_to_bulk_message.bulk_message_id,
                    'group_ids': this.selected_groups.map(g => g.value),
                    'group_names': this.selected_groups.map(g => g.text),
                    'import_execution_mode': this.import_execution_mode,
                },

                'title': 'Listing customers in group(s)',
                'finished_button_text': 'Preview Recipients',
                'failed_button_text': 'Cancel',

                status: (pte) => {
                    return 'Customer phone numbers: ' + pte.progress_counter;
                },

                on_finished: (pte) => {
                    const query = {
                        'bulk_message_id': pte.output.bulk_message_id,
                        'return_to': 'view-bulk-message',
                    };
                    if (this.is_remove_mode) {
                        query.mode = 'remove';
                    }
                    this.$router.push({
                        'name': 'bulk-message-recipients-import-upload',
                        'params': {
                            'organization_id': pte.output.organization_id,
                            'import_id': pte.output.import_id
                        },
                        'query': query
                    });
                },

                on_failed: () => {
                    this.$rc.alert('Error', 'Unable to add customers to bulk message');
                },
            });
        },
    }
}
</script>

<style lang="scss" scoped>
.enhanced-groups-table {
    ::v-deep .v-data-table__wrapper {
        border-radius: 8px;
    }

    ::v-deep tbody tr {
        transition: all 0.2s ease-in-out;

        &:hover {
            background-color: rgba(var(--v-primary-base), 0.04) !important;
        }

        &.table-row--selected {
            background-color: rgba(var(--v-primary-base), 0.08) !important;

            &:hover {
                background-color: rgba(var(--v-primary-base), 0.12) !important;
            }
        }

        &.table-row--selectable {
            cursor: pointer;
        }
    }

    ::v-deep .group-info-column {
        padding: 16px !important;
    }

    ::v-deep th {
        font-weight: 600 !important;
        color: rgba(0, 0, 0, 0.87) !important;
        background-color: #fafafa !important;
    }

    // Group-specific info column styling
    ::v-deep .group-info-column {
        padding: 16px !important;
    }
}

.group-row-content {
    transition: all 0.2s ease-in-out;
    padding: 8px 0;

    &--selected {
        .group-name {
            color: var(--v-primary-base);
            font-weight: 600;
        }
    }
}

.group-name {
    font-weight: 500;
    font-size: 16px;
    line-height: 1.3;
    margin-bottom: 4px;
}

.group-description {
    font-size: 14px;
    line-height: 1.4;
    color: rgba(0, 0, 0, 0.6);
}

.text-truncate {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.status-chip {
    font-weight: 500 !important;
    font-size: 12px !important;
}

.id-chip {
    font-family: monospace !important;
    font-size: 11px !important;
}
</style>
